import { WORKFLOW_TRIGGER_CATALOG } from "./workflow-trigger-catalog";
import type { EventBackedTriggerType } from "./workflow-trigger-catalog";
import type { ActionType, ConditionType, TriggerType } from "./workflow-node-types";

/**
 * Whether a simulation world runs a workflow exactly as its source workspace
 * would. A simulated run must look to the model like a production run, so a
 * workflow that needs something a world cannot yet do faithfully is refused
 * before it starts, with the gap named, and never runs with less.
 */

/** `gap` finishes "it ..." for a trigger and "its step ..." for a step. */
export type WorldSupport = "faithful" | { gap: string };

const EVENT_TRIGGER_WORLD_SUPPORT = Object.fromEntries(
  Object.keys(WORKFLOW_TRIGGER_CATALOG).map((type) => [
    type,
    {
      gap: `starts from the ${WORKFLOW_TRIGGER_CATALOG[type as EventBackedTriggerType].label.toLowerCase()} event`,
    },
  ]),
) as Record<EventBackedTriggerType, WorldSupport>;

export const TRIGGER_WORLD_SUPPORT: Record<TriggerType, WorldSupport> = {
  ...EVENT_TRIGGER_WORLD_SUPPORT,
  email_received: "faithful",
  tag_applied: { gap: "starts when a tag is added or removed" },
  task_updated: { gap: "starts when a task is updated" },
  schedule: { gap: "starts on a schedule" },
  manual: { gap: "is started by hand" },
};

export const CONDITION_WORLD_SUPPORT: Record<ConditionType, WorldSupport> = {
  field_equals: "faithful",
  else_branch: "faithful",
  placeholder: { gap: "is an unfinished branch" },
};

export const ACTION_WORLD_SUPPORT: Record<ActionType, WorldSupport> = {
  run_agent: "faithful",
  send_email: { gap: "sends email" },
  create_task: { gap: "creates a task" },
  human_review: { gap: "asks a person for a review" },
  add_tag: { gap: "adds a tag" },
  remove_tag: { gap: "removes a tag" },
  run_extractor: { gap: "runs an extractor" },
  run_classifier: { gap: "runs a classifier" },
  webhook: { gap: "calls a webhook" },
};

/** A requirement of the package a world was installed from, as far as the world's record knows it. */
type WorldRequirement = {
  /** The requirement's package key. Never shown; safe to log, because it carries no name. */
  key: string;
  /** Its kind as the package declares it (`connection`, `mailbox`), or null when the record does not know the key. */
  kind: string | null;
  /** The name an admin knows it by: the requirement's label or the folder's name. Null when unknown. */
  label: string | null;
};

/**
 * Why a simulation world cannot run an agent as its source workspace does. Either the agent
 * has something there that the world does not back (access it was given, a credential it may
 * use, a folder staged for it, something its instructions or skills mention), or it holds
 * access to a simulated connection some of whose tools nothing answers, or the world cannot
 * tell: its installation record or the version the run would pin cannot be read.
 */
export type WorldAgentMissing =
  | ({ use: "access" | "credential" | "staged_folder" } & WorldRequirement)
  | ({ use: "mention"; place: "instructions" | "skills" } & WorldRequirement)
  /** `provider` names the tool families with no mock, as a person knows them ("OneDrive"). */
  | ({ use: "unmocked"; provider: string } & WorldRequirement)
  | { use: "record_unreadable" }
  | { use: "version_missing" };

/**
 * What the world holds for an agent. `published` means a run can pin a version: a user agent
 * that was published, or any other kind, for which the runtime writes one from the agent's
 * definition. An agent absent from the environment is not in the world.
 */
export type WorldAgent = {
  name: string;
  state: "published" | "unpublished" | "archived";
  missing: WorldAgentMissing | null;
};

export type WorkflowWorldEnvironment = {
  agents: ReadonlyMap<string, WorldAgent>;
};

export type WorldBlocker = {
  /** What kind of blocker this is. Logs carry this and the ids below, never the sentence. */
  code:
    | "unsupported_node"
    | "unknown_node"
    | "no_trigger"
    | "unresolved_reference"
    | "agent_missing"
    | "agent_archived"
    | "agent_unpublished"
    | "agent_lacks";
  nodeId: string;
  nodeType: string | null;
  nodeName: string;
  /** The agent a `run_agent` step names, and what it lacks, when the blocker is about one. */
  agentPublicId?: string;
  missing?: WorldAgentMissing;
  subject: "trigger" | "step";
  /** `simulations`: no simulation can do this yet. `this_world`: this world lacks something. */
  scope: "simulations" | "this_world";
  /** Finishes "it ..." for a trigger and "its step ..." for a step. */
  gap: string;
  /** One imperative sentence that makes the workflow simulatable. */
  resolution: string;
};

declare const workflowStartCheck: unique symbol;

/** Proof that a workflow passed the start check. Only `workflowWorldReadiness` issues one. */
export type WorkflowWorldStartCheck = { readonly [workflowStartCheck]: true };

export type WorldBlockers = readonly [WorldBlocker, ...WorldBlocker[]];

export type WorkflowWorldReadiness =
  | { ready: true; check: WorkflowWorldStartCheck }
  | { ready: false; blockers: WorldBlockers };

const issuedChecks = new WeakSet<object>();

/** True only for a check this module issued, so a value cast to the type admits nothing. */
export function isWorkflowWorldStartCheck(value: unknown): value is WorkflowWorldStartCheck {
  return typeof value === "object" && value !== null && issuedChecks.has(value);
}

const REMOVE_STEP = "Remove that step to simulate this workflow.";
const USE_EMAIL_TRIGGER = "Start it from an incoming email to simulate it.";

/** A reference installation could not resolve to something in the destination workspace. */
const UNRESOLVED_REFERENCE = /^(?:unsupported|binding|package):([^\s/]+)(?:\/\S*)?$/;

const REFERENCE_NOUNS: Record<string, string> = {
  agents: "an agent",
  classifiers: "a classifier",
  code_defined_agents: "a built-in agent",
  extractors: "an extractor",
  mailbox: "a mailbox",
  personal_agents: "a personal agent",
  records: "a record",
  tags: "a tag",
  user: "a person",
  workflow_files: "a file",
};

const REQUIREMENT_NOUNS: Record<string, string> = {
  connection: "connection",
  credential: "credential",
  integration: "integration",
  linked_account: "linked account",
  mailbox: "mailbox",
  mcp_server: "MCP server",
  sharepoint_folder: "SharePoint folder",
};

const ANOTHER_AGENT = "point the step at another agent";

/**
 * What an agent lacks, as a clause about the agent, and what a person editing the workflow
 * can do about it. Where the lack itself can be fixed in the world, that comes first.
 */
function describeWorldAgentMissing(missing: WorldAgentMissing) {
  if (missing.use === "record_unreadable") {
    return {
      gap: "cannot be checked because this simulation world's installation record cannot be read",
      resolution: "Create the simulation world again.",
    };
  }
  if (missing.use === "version_missing") {
    return {
      gap: "has a published version that cannot be found",
      resolution: `Publish that agent again in this simulation world, or ${ANOTHER_AGENT}.`,
    };
  }
  if (missing.use === "unmocked") {
    const connection =
      missing.label === null ? "a connection" : `the connection "${missing.label}"`;
    return {
      gap: `uses ${connection}, whose ${missing.provider} tools could not be simulated when this simulation world was created`,
      resolution: "Point that step at another agent, or remove the step.",
    };
  }
  const noun =
    missing.use === "staged_folder"
      ? "folder"
      : (REQUIREMENT_NOUNS[missing.kind ?? ""] ?? "resource");
  // A key the record does not know is an internal name, so the sentence does without one.
  const named =
    missing.label === null
      ? "a resource this simulation world has no record of"
      : `the ${noun} "${missing.label}" that this simulation world does not have`;
  if (missing.use === "mention") {
    return missing.place === "skills"
      ? {
          gap: `mentions in one of its skills ${named}`,
          resolution: `Remove that mention from the skill, or ${ANOTHER_AGENT}.`,
        }
      : {
          gap: `mentions in its instructions ${named}`,
          resolution: `Remove that mention and publish the agent again, or ${ANOTHER_AGENT}.`,
        };
  }
  return {
    gap: `uses ${named}`,
    resolution:
      missing.use === "staged_folder" && missing.label !== null
        ? `Stage a folder named "${missing.label}" on that agent and publish it again, or ${ANOTHER_AGENT}.`
        : `Point that step at another agent, or remove the step.`,
  };
}

/**
 * The refusal for an agent a tool call would start: a task's assignee, or a delegated child.
 * It is read by the calling agent, which can only choose whom to start.
 */
export function describeWorldAgentRefusal(agentName: string, missing: WorldAgentMissing): string {
  const { gap } = describeWorldAgentMissing(missing);
  const resolution =
    missing.use === "record_unreadable"
      ? "Report that this simulation world must be created again."
      : "Choose another agent.";
  return `Agent "${agentName}" cannot start in this simulation world because it ${gap}. ${resolution}`;
}

/** What a log may say of what an agent lacks: the kind of thing and its key, never its name. */
export function worldAgentMissingLogFields(missing: WorldAgentMissing): Record<string, unknown> {
  if (missing.use === "record_unreadable" || missing.use === "version_missing") {
    return { use: missing.use };
  }
  return {
    use: missing.use,
    requirementKey: missing.key,
    requirementKind: missing.kind,
    ...(missing.use === "mention" ? { place: missing.place } : {}),
    // A tool family's product name ("OneDrive"), never a name the customer chose.
    ...(missing.use === "unmocked" ? { provider: missing.provider } : {}),
  };
}

/** What a log may say of a blocker: its kind and the ids involved, never the sentence. */
export function worldBlockerLogFields(blocker: WorldBlocker): Record<string, unknown> {
  return {
    code: blocker.code,
    nodeId: blocker.nodeId,
    nodeType: blocker.nodeType,
    ...(blocker.agentPublicId ? { agentPublicId: blocker.agentPublicId } : {}),
    ...(blocker.missing ? worldAgentMissingLogFields(blocker.missing) : {}),
  };
}

type AgentBlocker = Pick<WorldBlocker, "code" | "gap" | "resolution" | "missing">;

function agentBlocker(agent: WorldAgent | undefined): AgentBlocker | null {
  if (!agent) {
    return {
      code: "agent_missing",
      gap: "runs an agent that is not in this simulation world",
      resolution: "Choose an agent that exists in this simulation world.",
    };
  }
  const runs = `runs agent "${agent.name}", which`;
  if (agent.state === "archived") {
    return {
      code: "agent_archived",
      gap: `${runs} is archived`,
      resolution: "Unarchive that agent in this simulation world, or choose another agent.",
    };
  }
  if (agent.state === "unpublished") {
    return {
      code: "agent_unpublished",
      gap: `${runs} has no published version here`,
      resolution: "Publish that agent in this simulation world to simulate this workflow.",
    };
  }
  if (!agent.missing) return null;
  const { gap, resolution } = describeWorldAgentMissing(agent.missing);
  return { code: "agent_lacks", gap: `${runs} ${gap}`, resolution, missing: agent.missing };
}

/** `graph_json` is unvalidated jsonb, so nothing about a node's shape is assumed. */
type StoredNode = {
  id?: unknown;
  kind?: unknown;
  type?: unknown;
  name?: unknown;
  config?: unknown;
  conditions?: unknown;
};

function nodesOf(graph: unknown): StoredNode[] {
  const nodes = (graph as { nodes?: unknown } | null)?.nodes;
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node): StoredNode => (node && typeof node === "object" ? node : {}));
}

function supportOf(node: StoredNode): WorldSupport | null {
  const byType: Record<string, WorldSupport> | null =
    node.kind === "trigger"
      ? TRIGGER_WORLD_SUPPORT
      : node.kind === "condition"
        ? CONDITION_WORLD_SUPPORT
        : node.kind === "action"
          ? ACTION_WORLD_SUPPORT
          : null;
  if (!byType || typeof node.type !== "string" || !Object.hasOwn(byType, node.type)) return null;
  return byType[node.type] ?? null;
}

/** The noun for the first unresolved reference anywhere in a value, or null when there is none. */
function unresolvedReference(value: unknown): string | null {
  if (typeof value === "string") {
    const family = UNRESOLVED_REFERENCE.exec(value.trim())?.[1];
    return family ? (REFERENCE_NOUNS[family] ?? "something") : null;
  }
  if (value === null || typeof value !== "object") return null;
  for (const nested of Object.values(value)) {
    const found = unresolvedReference(nested);
    if (found) return found;
  }
  return null;
}

function runAgentId(node: StoredNode): string | null {
  if (node.kind !== "action" || node.type !== "run_agent") return null;
  const id = (node.config as { agentPublicId?: unknown } | null)?.agentPublicId;
  return typeof id === "string" ? id.trim() : "";
}

/** The agents a graph's `run_agent` steps name, so a caller can load their state for the check. */
export function workflowRunAgentIds(graph: unknown): string[] {
  const ids = new Set<string>();
  for (const node of nodesOf(graph)) {
    const id = runAgentId(node);
    if (id) ids.add(id);
  }
  return [...ids];
}

function blockerFor(node: StoredNode, environment: WorkflowWorldEnvironment): WorldBlocker | null {
  const subject = node.kind === "trigger" ? "trigger" : "step";
  const at = {
    nodeId: typeof node.id === "string" ? node.id : "",
    nodeType: typeof node.type === "string" ? node.type : null,
    nodeName: typeof node.name === "string" && node.name.trim() ? node.name.trim() : "Untitled",
    subject,
  } as const;
  const unsupported = {
    ...at,
    scope: "simulations",
    resolution: subject === "trigger" ? USE_EMAIL_TRIGGER : REMOVE_STEP,
  } as const;

  const support = supportOf(node);
  if (!support) {
    return { ...unsupported, code: "unknown_node", gap: "is of a kind simulations do not know" };
  }
  if (support !== "faithful") return { ...unsupported, code: "unsupported_node", gap: support.gap };

  const reference = unresolvedReference([node.config, node.conditions]);
  if (reference) {
    return {
      ...at,
      code: "unresolved_reference",
      scope: "this_world",
      gap: `uses ${reference} that was not copied into this simulation world`,
      resolution:
        subject === "trigger"
          ? "Point the trigger at one that exists in this simulation world."
          : "Point that step at one that exists in this simulation world, or remove the step.",
    };
  }

  const agentId = runAgentId(node);
  if (agentId === null) return null;
  const blocker = agentBlocker(environment.agents.get(agentId));
  return blocker ? { ...at, scope: "this_world", agentPublicId: agentId, ...blocker } : null;
}

/**
 * The start check for a workflow in a simulation world. Every node counts, reachable or
 * not, because which branch a run takes is not known until it runs.
 */
export function workflowWorldReadiness(
  graph: unknown,
  environment: WorkflowWorldEnvironment,
): WorkflowWorldReadiness {
  const nodes = nodesOf(graph);
  const blockers = nodes.flatMap((node) => blockerFor(node, environment) ?? []);
  // An empty or malformed graph has nothing in the way, and must not pass for that reason.
  if (!nodes.some((node) => node.kind === "trigger")) {
    blockers.unshift({
      code: "no_trigger",
      nodeId: "",
      nodeType: null,
      nodeName: "Untitled",
      subject: "trigger",
      scope: "simulations",
      gap: "has no trigger",
      resolution: USE_EMAIL_TRIGGER,
    });
  }
  const [first, ...rest] = blockers;
  if (first) return { ready: false, blockers: [first, ...rest] };
  const check = Object.freeze({}) as WorkflowWorldStartCheck;
  issuedChecks.add(check);
  return { ready: true, check };
}

/**
 * The refusal an admin reads: what is wrong, then how to resolve it. It names the first
 * blocker only, so the message stays two sentences however many steps are in the way.
 */
export function describeWorldRefusal(workflowName: string, blockers: WorldBlockers): string {
  const [first] = blockers;
  const where = first.subject === "trigger" ? "it" : `its step "${first.nodeName}"`;
  const reach = first.scope === "simulations" ? "in a simulation yet" : "in this simulation world";
  return `Workflow "${workflowName}" cannot run ${reach} because ${where} ${first.gap}. ${first.resolution}`;
}

/**
 * The requirements an authored document still refers to by `binding:` key: reference chips
 * installation could not point at anything in the destination. Only structured chips count,
 * as in the installer; prose that happens to contain the text is not a reference.
 */
export function unboundDocumentReferences(content: string | null | undefined): string[] {
  if (!content?.trim().startsWith("{")) return [];
  let root: unknown;
  try {
    root = JSON.parse(content);
  } catch {
    return [];
  }
  const keys = new Set<string>();
  const visit = (value: unknown, depth: number) => {
    // The installer refuses a document nested deeper than this, so nothing installed is.
    if (depth > 64 || value === null || typeof value !== "object") return;
    const node = value as { type?: unknown; attrs?: { id?: unknown } | null };
    const id = node.type === "aopReference" ? node.attrs?.id : undefined;
    if (typeof id === "string" && id.startsWith("binding:")) keys.add(id.slice(8));
    for (const child of Object.values(value)) visit(child, depth + 1);
  };
  visit(root, 0);
  return [...keys];
}
